/**
 * SiteOfCareOutputField - Output component for sites_of_care array field
 *
 * Displays site of care output data as cards with name and network status.
 * Uses path-scoped enum resolution for network_status formatting.
 *
 * Exports:
 * - SiteOfCareOutputField: Main output component
 * - SiteOfCareOutputFieldPreview: Preview component for table cells
 */

import React from 'react';

import { getCallSourceId } from '@/utils/evidence';
import { humanize } from '@/utils/fields';

import {
  FieldPathProvider,
  ItemDispositionProvider,
  ReviewProvider,
  SchemaProvider,
} from './context';
import { PillPreviewList, type PreviewPill } from './PillPreviewList';
import { Field } from './sections';
import type { OutputArrayProps, PreviewProps } from './types';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Site of care output item shape (defined locally, schema provides field definitions at runtime)
 */
interface SiteOfCareOutputItem {
  name?: string;
  network_status?: string;
  coverage_status?: boolean;
  coinsurance?: number;
  copay?: number;
}

// =============================================================================
// MAIN OUTPUT COMPONENT
// =============================================================================

export const SiteOfCareOutputField: React.FC<OutputArrayProps> = ({
  data,
  outputMetadata,
  extractedMetadata,
  extractedData,
  onNavigateToCall,
  fieldId = 'sites_of_care',
  isHumanAudit,
  onAcceptExtracted,
  dispositionFields,
  currentCallId,
}) => {
  const items = (Array.isArray(data) ? data : []) as SiteOfCareOutputItem[];

  if (items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic py-2">
        No site of care data available
      </div>
    );
  }

  const outputFieldMeta = outputMetadata?.fields?.[fieldId] as any;
  const extractedFieldMeta = extractedMetadata?.fields?.[fieldId] as any;
  const outputSourceCallId = getCallSourceId(outputFieldMeta);
  const extractedSourceCallId = getCallSourceId(extractedFieldMeta);
  const extractedDataArray = Array.isArray(extractedData) ? extractedData : [];

  return (
    <SchemaProvider>
      <div className="space-y-3">
        {items.map((item, index) => {
          const itemLabel = item.name || `Site ${index + 1}`;

          return (
            // Without this, every Field below sees an empty DispositionContext,
            // so `hasDisposition` is false and NO missing-field icon renders —
            // however many `sites_of_care[N].*` paths the disposition reports.
            // The enclosing accordion's "X/Y captured" badge reads
            // dispositionFields directly, which is why the count was right while
            // the per-field markers were absent.
            <ItemDispositionProvider
              key={item.name || `site-${index}`}
              arrayName={fieldId}
              index={index}
              dispositionFields={dispositionFields}
            >
              <ReviewProvider
                outputItemMeta={outputFieldMeta?.[index]}
                outputSourceCallId={outputSourceCallId}
                extractedItemMeta={extractedFieldMeta?.[index]}
                extractedSourceCallId={extractedSourceCallId}
                extractedItemData={extractedDataArray[index]}
                onNavigateToCall={onNavigateToCall}
                isHumanAudit={isHumanAudit}
                onAcceptExtracted={onAcceptExtracted}
                currentCallId={currentCallId}
              >
                <div className="border rounded-md bg-muted/30 px-3 py-2">
                  <div className="text-sm font-medium mb-2">{itemLabel}</div>
                  <FieldPathProvider prefix="sites_of_care">
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Name" value={item.name} fieldId="name" />
                      <Field
                        label="Network Status"
                        value={item.network_status}
                        fieldId="network_status"
                      />
                      <Field
                        label="Coverage Status"
                        value={item.coverage_status}
                        fieldId="coverage_status"
                      />
                      <Field label="Coinsurance" value={item.coinsurance} fieldId="coinsurance" />
                      <Field label="Copay" value={item.copay} fieldId="copay" />
                    </div>
                  </FieldPathProvider>
                </div>
              </ReviewProvider>
            </ItemDispositionProvider>
          );
        })}
      </div>
    </SchemaProvider>
  );
};

// =============================================================================
// PREVIEW COMPONENT
// =============================================================================

/** Item shape for preview (defined locally, schema provides field definitions at runtime) */
interface SiteOfCarePreviewItem {
  name?: string;
  network_status?: string;
  coverage_status?: boolean;
  coinsurance?: number;
  copay?: number;
}

/**
 * Format a network status enum value to a short badge label.
 * Falls back to humanized enum string if not recognized.
 */
export function formatNetworkStatusBadge(status?: string): string | null {
  if (!status) return null;
  if (status.includes('ONLY_IN_NETWORK')) return 'In-Network';
  if (status.includes('ONLY_OUT_OF_NETWORK')) return 'Out-of-Network';
  if (status.includes('NOT_APPLICABLE')) return 'N/A';
  // Fallback for UNKNOWN, the one value with no short form. The `ONLY_` segment
  // is part of the prefix (SiteOfCareNetworkStatusOnly) and must be stripped
  // too — leaving it in rendered "ONLY UNKNOWN". Optional, to tolerate a value
  // stored under an earlier prefix.
  return humanize(status.replace(/^SITE_OF_CARE_NETWORK_STATUS_(?:ONLY_)?/, ''));
}

/**
 * SiteOfCareOutputFieldPreview - Pill-based preview component for sites_of_care output array field
 *
 * Displays sites of care as pills showing name with a network status badge.
 */
export const SiteOfCareOutputFieldPreview: React.FC<PreviewProps> = ({ data, maxItems = 2 }) => {
  const items = (Array.isArray(data) ? data : []) as SiteOfCarePreviewItem[];

  const pills: PreviewPill[] = items.flatMap((item, index) => {
    if (!item.name) return [];
    const statusLabel = formatNetworkStatusBadge(item.network_status);

    return [
      {
        key: item.name || `site-${index}`,
        text: statusLabel ? `${item.name} · ${statusLabel}` : item.name,
        // The status keeps its muted styling inline; the tooltip uses the flat `text`.
        node: (
          <>
            {item.name}
            {statusLabel && <span className="ml-1 text-muted-foreground">· {statusLabel}</span>}
          </>
        ),
      },
    ];
  });

  return <PillPreviewList pills={pills} maxItems={maxItems} />;
};
